import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import GenerateConfirmDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/ConfirmationDialogs/GenerateConfirmDialog';
import StageCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard';
import { openGenerateConfirm } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/Signals/generateConfirmSignal';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import { Sparkles } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const stagesStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const launchStyle = css`
	display: flex;
	justify-content: flex-start;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		& > button {
			width: 100%;
		}
	}
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
	/** Whether the visitor may launch (permission, dossier not archived). */
	readonly canLaunch: boolean;
};

/**
 * The stages in order with the state of each document, then the one « Générer » button while the
 * preparation is open: disabled, it reads the server's reason instead of its label. The server decides
 * every state; nothing is recomputed here.
 */
const GenerateSection: React.FC<Props> = ({ subject, preparation, canLaunch }) => {
	const showLaunch = preparation.isOpen && canLaunch;
	const label =
		!preparation.canGenerate && preparation.blockedReason !== null
			? preparation.blockedReason
			: 'Générer';

	return (
		<React.Fragment>
			<div css={stagesStyle}>
				{preparation.documentStages.map((stage) => (
					<StageCard
						key={stage.stage}
						subject={subject}
						preparationId={preparation.id}
						stage={stage}
						elementCount={preparation.sources.length}
						canLaunch={canLaunch}
					/>
				))}
			</div>
			<ConditionalLoader data={showLaunch}>
				<div css={launchStyle}>
					<Button
						type="button"
						icon={<Sparkles size={16} />}
						label={label}
						disabled={!preparation.canGenerate}
						onClick={openGenerateConfirm}
					/>
				</div>
				<GenerateConfirmDialog subject={subject} preparation={preparation} />
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default GenerateSection;
